/**
 * useTableSelection — Table-molecule-owned row selection primitive.
 *
 * Ports the semantics of A8 (usePageSelectionState) and A9
 * (useSelectAllEntireSelection) from `@hooks/listings` into the `@ui` molecule
 * layer. A `@ui` molecule must never import from `@hooks/listings`; the
 * dependency runs the other way.
 *
 * Key behavioral contracts (preserved from A8/A9):
 * - `toggleItem` exits entire mode.
 * - `selectCurrentPage` replaces the set; calling it again with the same full
 *   set clears (A8 parity). Exits entire mode.
 * - `selectAllEntirely` sets the `selectAllEntire` flag and selects the current
 *   page ids so visible rows stay checked. No-op in `'page'` mode.
 * - `clearSelection` clears BOTH `selectedIds` AND `selectAllEntire`.
 * - `resetKey` change clears selection; mounting does NOT fire a spurious clear.
 * - `onChange` is called from an effect (committed state), not from inside
 *   setters.
 *
 * ⚠ Decision 9: `selectionCount` must derive from `selectedIds` directly and
 * never from a loading-suppressed `selectableData` array. Table.tsx suppresses
 * the header checkbox while `isLoading` but the toolbar badge must stay stable
 * during background refetches (Series passes `isLoading={isFetching}`).
 *
 * Naming note: the internal derived count is `selectionCount` (not
 * `selectedCount`) to avoid shadowing the `const selectedCount` already bound
 * at Table.tsx:612. The public `TableSelectionState.selectedCount` field still
 * uses `selectedCount`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
export function useTableSelection({ data, getItemId, mode, serverTotalItems, resetKey, onChange }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAllEntire, setSelectAllEntire] = useState(false);
    // ── Derived values ────────────────────────────────────────────────────────
    const selectedRows = useMemo(() => data.filter((item) => selectedIds.includes(getItemId(item))), [data, getItemId, selectedIds]);
    /**
     * Internal name `selectionCount` avoids shadowing `const selectedCount` at
     * Table.tsx:612. The public `TableSelectionState.selectedCount` exposes this
     * value under the frozen API name.
     *
     * Derives from `selectedIds` directly — never from a loading-suppressed
     * array — so the toolbar badge stays stable during background refetches
     * (decision 9).
     */
    const selectionCount = selectAllEntire ? serverTotalItems !== null && serverTotalItems !== void 0 ? serverTotalItems : selectedIds.length : selectedIds.length;
    // ── Actions ───────────────────────────────────────────────────────────────
    const clearSelection = useCallback(() => {
        setSelectedIds([]);
        setSelectAllEntire(false);
    }, []);
    const toggleItem = useCallback((id) => {
        setSelectAllEntire(false);
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }, []);
    const selectCurrentPage = useCallback((ids, options) => {
        setSelectAllEntire(false);
        setSelectedIds((prev) => {
            if (options === null || options === void 0 ? void 0 : options.replace)
                return ids;
            // A8 parity: calling selectCurrentPage with the same full set clears.
            const allAlreadySelected = ids.length > 0 && ids.length === prev.length && ids.every((id) => prev.includes(id));
            return allAlreadySelected ? [] : ids;
        });
    }, []);
    const selectAllEntirely = useCallback(() => {
        if (mode === 'page')
            return;
        setSelectAllEntire(true);
        setSelectedIds(data.map(getItemId));
    }, [mode, data, getItemId]);
    // ── resetKey effect ───────────────────────────────────────────────────────
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        clearSelection();
        // clearSelection is stable (no deps); resetKey is the only trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);
    // ── onChange effect ───────────────────────────────────────────────────────
    const composedState = useMemo(() => ({
        selectedIds,
        selectedRows,
        selectAllEntire,
        selectedCount: selectionCount,
        clearSelection
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, selectedRows, selectAllEntire, selectionCount]);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    });
    const isFirstOnChange = useRef(true);
    useEffect(() => {
        var _a;
        if (isFirstOnChange.current) {
            isFirstOnChange.current = false;
            return;
        }
        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, composedState);
    }, [composedState]);
    return Object.assign(Object.assign({}, composedState), { toggleItem,
        selectCurrentPage,
        selectAllEntirely });
}
